import { ChevronLeft, ChevronRight, Library } from 'lucide-react'
import { useDraggable } from '@dnd-kit/react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import type { FieldType } from './types'
import { FIELD_ICONS, FIELD_LABELS, PALETTE_GROUPS, PROCESS_ACCENT } from './field-config'

interface PaletteItemProps {
  fieldType: FieldType
  onAdd: () => void
  iconOnly?: boolean
}

function PaletteItem({ fieldType, onAdd, iconOnly }: PaletteItemProps) {
  const { ref, isDragging } = useDraggable({
    id: `palette-${fieldType}`,
    data: { origin: 'palette', fieldType, label: FIELD_LABELS[fieldType] },
  })

  const Icon = FIELD_ICONS[fieldType]
  // Matches the amber the canvas card uses, so the palette entry and the field
  // it drops read as the same thing.
  const iconClass = fieldType === 'process' ? PROCESS_ACCENT.icon : 'text-muted-foreground'

  if (iconOnly) {
    return (
      <div
        ref={ref}
        onClick={onAdd}
        title={FIELD_LABELS[fieldType]}
        // Field labels are not unique on the page — "Process" is also a nav
        // destination — so the palette entry needs an identity of its own.
        data-testid={`palette-item-${fieldType}`}
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-md cursor-grab select-none',
          'hover:bg-accent transition-colors',
          isDragging && 'opacity-40',
        )}
      >
        <Icon className={cn('h-4 w-4', iconClass)} />
      </div>
    )
  }

  return (
    <div
      ref={ref}
      onClick={onAdd}
      data-testid={`palette-item-${fieldType}`}
      className={cn(
        'flex items-center gap-2.5 px-3 py-2 rounded-md cursor-grab select-none text-sm',
        'border border-transparent hover:border-border hover:bg-accent transition-colors',
        isDragging && 'opacity-40'
      )}
    >
      <Icon className={cn('h-4 w-4 shrink-0', iconClass)} />
      <span className='text-foreground'>{FIELD_LABELS[fieldType]}</span>
    </div>
  )
}

interface Props {
  onAddField: (type: FieldType) => void
  /** Opens the field-library picker. When omitted, the library entry is hidden. */
  onOpenLibrary?: () => void
  /** Current panel width in px (ignored when collapsed). */
  width?: number
  collapsed?: boolean
  onToggleCollapse?: () => void
  /** Called on mousedown of the right-edge resize handle. */
  onResizeStart?: (e: React.MouseEvent) => void
}

export function FieldPalette({ onAddField, onOpenLibrary, width, collapsed, onToggleCollapse, onResizeStart }: Props) {
  if (collapsed) {
    return (
      <aside
        className='border-r bg-muted/20 flex flex-col items-center shrink-0 overflow-hidden'
        style={{ width: 40 }}
      >
        <div className='flex justify-center py-2 border-b w-full'>
          <button
            className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent'
            onClick={onToggleCollapse}
            title='Expand field palette'
          >
            <ChevronRight className='h-4 w-4 text-muted-foreground' />
          </button>
        </div>
        <div className='flex flex-col items-center gap-0.5 p-1 mt-2 overflow-y-auto flex-1'>
          {PALETTE_GROUPS.flatMap(g => g.items).map(type => (
            <PaletteItem
              key={type}
              fieldType={type}
              onAdd={() => onAddField(type)}
              iconOnly
            />
          ))}
        </div>
      </aside>
    )
  }

  return (
    <aside
      className='relative border-r bg-muted/20 flex flex-col overflow-y-auto shrink-0'
      style={{ width: width ?? 208 }}
    >
      {/* Resize handle on right edge */}
      {onResizeStart && (
        <div
          className='absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/20 z-10'
          onMouseDown={onResizeStart}
        />
      )}
      <div className='px-3 py-3 border-b flex items-center gap-1'>
        <p className='text-xs font-semibold text-muted-foreground uppercase tracking-wide flex-1'>
          Field Types
        </p>
        <button
          className='flex h-6 w-6 items-center justify-center rounded-md hover:bg-accent shrink-0'
          onClick={onToggleCollapse}
          title='Collapse field palette'
        >
          <ChevronLeft className='h-3.5 w-3.5 text-muted-foreground' />
        </button>
      </div>
      {onOpenLibrary && (
        <div className='px-2 pt-3'>
          <Button
            variant='outline'
            size='sm'
            className='w-full justify-start'
            onClick={onOpenLibrary}
          >
            <Library className='mr-2 h-4 w-4' />
            Field Library
          </Button>
        </div>
      )}
      <div className='p-2 space-y-5 py-4'>
        {PALETTE_GROUPS.map(group => (
          <div key={group.title}>
            <p className='text-xs text-muted-foreground px-3 mb-1'>{group.title}</p>
            <div className='space-y-0.5'>
              {group.items.map(type => (
                <PaletteItem
                  key={type}
                  fieldType={type}
                  onAdd={() => onAddField(type)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className='mt-auto p-4 border-t'>
        <p className='text-xs text-muted-foreground leading-relaxed'>
          Drag fields onto the canvas or click to add.
        </p>
      </div>
    </aside>
  )
}
